import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowLeft, Pencil, Info, Loader2 } from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { FormField } from '../components/FormField';
import { formatHelpText } from '../config/helptext-config';
import { playbookFormHelpText } from '../config/helptext-config';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Playbook } from '../types';
import ToolsSelect from '../agents/ToolsSelect';
import AgentSelect from './AgentsSelect';
import { AccessControlSection } from '../components/AccessControlSection';
import { useAuth } from '../contexts/AuthContext';
import { useUsersApi } from '../apis/users-apis';
import AccessDisplay from '../components/AccessDisplay';
import OwnerSelection from '../components/OwnerSelection';
import ProductSelect from './ProductSelect';
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from '../config/api-config';
import { ExecutionRecordsDisplay } from '../components/ExecutionRecordsDisplay';
import { Dialog } from '../components/ui/Dialog';

export interface PlaybookFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

interface AccessControl {
  editAccessType: string;
  editAllowedUsers: string[];
  viewAccessType: string;
  viewAllowedUsers: string[];
}

interface PlaybookFormProps {
  playbook: Playbook;
  originalPlaybook?: Playbook | null;
  onPlaybookChange: (pkg: Playbook) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingPlaybooks?: Playbook[];
  isSaving?: boolean;
}

// View Mode Field Component
const ViewModeField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="mb-6">
    <h3 className="text-sm font-medium text-gray-500 mb-2">{label}</h3>
    {typeof value === 'string' ? (
      <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap border border-gray-200">
        {value || 'No content provided'}
      </div>
    ) : (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        {value}
      </div>
    )}
  </div>
);

interface UserAPIResponse {
  users: {
    userId: string;
    email: string;
    isAdmin: boolean;
    isActive: boolean;
  }[];
}

const SaveOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      <span className="text-lg font-medium">Saving Playbook... Please wait</span>
    </div>
  </div>
);

const DiscardChangesDialog = ({ isOpen, onClose, onDiscard }: { isOpen: boolean; onClose: () => void; onDiscard: () => void }) => (
  <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-medium mb-4">Discard Changes?</h3>
        <p className="text-gray-600 mb-6">You have unsaved changes. Are you sure you want to discard them?</p>
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onDiscard}>
            Discard
          </Button>
        </div>
      </div>
    </div>
  </Dialog>
);

const PlaybookForm = forwardRef<PlaybookFormRef, PlaybookFormProps>(function PlaybookForm(props, ref) {
  const {
    playbook: pkg,
    onPlaybookChange,
    onSubmit,
    onCancel,
    isEditing = false,
    readOnly = false,
    onEditClick,
    onDiscardChanges,
    hasUnsavedChanges = false,
    tagSuggestions = [],
    existingPlaybooks,
    setIsReadOnly
  } = props;

  const [isAdmin, setIsAdmin] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tagManagementRef = useRef<TagManagementRef>(null);
  const { user } = useAuth();
  const usersApi = useUsersApi();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [users, setUsers] = useState<{ userId: string, email: string }[]>([]);

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false);
    if (onDiscardChanges) {
      onDiscardChanges();
    }
  };

  const [isCorePlaybook, setIsCorePlaybook] = useState(pkg.isCore || false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>('');

  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const usersData = await usersApi.getUsers();
          const userData = usersData.find(u => u.userId === user.sub);
          setIsAdmin(userData?.isAdmin || false);

          // Find the admin service account ID
          const adminServiceAccount = usersData.find(u =>
            u.email === ADMIN_SERVICE_USER
          );

          if (adminServiceAccount) {
            setAdminServiceUserId(adminServiceAccount.userId);
          } else {
            console.warn(`Admin service account ${ADMIN_SERVICE_USER} not found in users list`);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsAdmin(false);
        }
      }
    };

    fetchAdminStatus();
  }, [user?.sub]);

  useEffect(() => {
    // When isCorePlaybook is enabled, set default access control to "Owner Only"
    // and set owner to admin service account
    if (isCorePlaybook) {
      onPlaybookChange({
        ...pkg,
        isCore: true,
        ownerId: adminServiceUserId || pkg.ownerId,
        accessControl: {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      });
    } else {
      onPlaybookChange({
        ...pkg,
        isCore: false
      });
    }
  }, [isCorePlaybook, adminServiceUserId]);

  const hasEditAccess = (accessControl: AccessControl) => {
    const currentUserId = user?.sub;
    const isOwner = pkg.ownerId === currentUserId;

    if (!currentUserId) return false;

    // Admins always have edit access
    if (isAdmin) return true;

    // Owners always have edit access to their own playbooks
    if (isOwner) return true;

    // For non-admin, non-owner users, check access control settings
    const { editAccessType, editAllowedUsers } = accessControl;

    switch (editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return editAllowedUsers.includes(currentUserId);
      case 'Owner Only':
        return false;
      default:
        return false;
    }
  };

  const checkNameExists = (name: string): boolean => {
    return existingPlaybooks?.some(existingPlaybook =>
      existingPlaybook.playbookName.toLowerCase() === name.toLowerCase() &&
      existingPlaybook.playbookId !== pkg.playbookId
    ) ?? false;
  };

  const validatePlaybookName = async (name: string): Promise<{ isValid: boolean; error: string }> => {
    if (!name.trim()) {
      return { isValid: false, error: 'Playbook Name is required' };
    }

    if (name.length > 63) {
      return { isValid: false, error: 'Playbook Name must be less than 64 characters' };
    }

    // Check for disallowed characters
    if (!/^[a-zA-Z0-9_\s-]+$/.test(name)) {
      return {
        isValid: false,
        error: 'Playbook Name can only contain letters, numbers, spaces, underscores, and hyphens'
      };
    }

    // Check for uniqueness
    if (checkNameExists(name)) {
      return {
        isValid: false,
        error: 'A Playbook with this name already exists. Please choose a different name.'
      };
    }

    return { isValid: true, error: '' };
  };

  const handleCorePlaybookChange = (checked: boolean) => {
    if (readOnly) return;
    setIsCorePlaybook(checked);
  };

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const newErrors: Record<string, string> = {};

      const nameValidation = await validatePlaybookName(pkg.playbookName);
      if (!nameValidation.isValid) {
        newErrors.playbookName = nameValidation.error;
      }

      if (!pkg.playbookProductName.trim()) {
        newErrors.playbookProductName = 'Playbook Product Name is required';
      }
      if (!pkg.playbookDescription.trim()) {
        newErrors.playbookDescription = 'Playbook Description is required';
      }
      if (!pkg.playbookWorkflow.trim()) {
        newErrors.playbookWorkflow = 'Playbook Workflow is required';
      }
      if (!pkg.playbookActiveTools?.length) {
        newErrors.playbookActiveTools = 'At least one Active Tool is required';
      }
      if (!pkg.playbookHelperAgents?.length) {
        newErrors.playbookHelperAgents = 'At least one Helper Agent is required';
      }

      // Add access control validation only if not a core playbook
      if (!isCorePlaybook) {
        if (!pkg.accessControl.viewAccessType) {
          newErrors.viewAccessType = 'View Access Type is required';
        }

        if (!pkg.accessControl.editAccessType) {
          newErrors.editAccessType = 'Edit Access Type is required';
        }
      }

      setErrors(newErrors);

      // If validation passed, process any pending tags
      if (Object.keys(newErrors).length === 0) {
        // Process pending tags if any
        if (tagManagementRef.current) {
          await tagManagementRef.current.processPendingTags();
        }
      }

      return Object.keys(newErrors).length === 0;
    },
    getErrors: () => errors
  }));

  const AccessControlViewSection = () => {
    const [ownerDetails, setOwnerDetails] = useState({ email: '', name: '' });

    useEffect(() => {
      const fetchOwnerDetails = async () => {
        try {
          const usersData = await usersApi.getUsers();
          const owner = usersData.find((user) => user.userId === pkg.ownerId);
          if (owner) {
            setOwnerDetails({
              email: owner.email,
              name: owner.email //TODO: Add name to user table
            });
          }
        } catch (error) {
          console.error('Error fetching owner details:', error);
        }
      };

      fetchOwnerDetails();
    }, [pkg.ownerId]);

    // For Core Playbooks, show a special message
    if (pkg.isCore) {
      return (
        <ViewModeField
          label="Access Control"
          value={
            <div className="flex items-center space-x-2 text-gray-700">
              <Info className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">Administrator Only</p>
                <p className="text-sm text-gray-600">
                  This Core Playbook is managed by administrators only.
                </p>
              </div>
            </div>
          }
        />
      );
    }

    return (
      <ViewModeField
        label="Access Control"
        value={
          <AccessDisplay
            viewAccessType={pkg.accessControl.viewAccessType}
            editAccessType={pkg.accessControl.editAccessType}
            viewAllowedUsers={pkg.accessControl.viewAccessType === 'Allowed Users'
              ? pkg.accessControl.viewAllowedUsers
              : []}
            editAllowedUsers={pkg.accessControl.editAccessType === 'Allowed Users'
              ? pkg.accessControl.editAllowedUsers
              : []}
            ownerEmail={ownerDetails.email}
            ownerName={ownerDetails.name}
          />
        }
      />
    );
  };

  const handleInputChange = async (field: keyof Playbook, value: any) => {
    if (readOnly) return;

    onPlaybookChange({
      ...pkg,
      [field]: value
    });

    // Immediate validation for playbookName field
    if (field === 'playbookName') {
      const validation = await validatePlaybookName(value);
      if (!validation.isValid) {
        setErrors(prev => ({
          ...prev,
          playbookName: validation.error
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.playbookName;
          return newErrors;
        });
      }
    }
  };

  const ListDisplay: React.FC<{
    items: any[];
    type: 'tool' | 'agent';
  }> = ({ items, type }) => {
    if (items.length === 0) {
      return <div className="text-gray-500 italic">No {type}s configured</div>;
    }

    return (
      <div className="space-y-4">
        {items.map((item) => (
          <div key={type === 'tool' ? item.toolId : item.agentId}
            className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="font-medium text-gray-900">
              {type === 'tool' ? item.toolName : item.agentName}
            </div>
            <p className="text-gray-600 mt-1">
              {type === 'tool' ? item.toolDescription : item.agentDescription}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderEditMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
          <CardTitle className="text-2xl">
            {pkg.playbookName}
          </CardTitle>
          <Button
            variant="outline"
            onClick={() => setIsReadOnly(true)}
            className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600 mt-2 sm:mt-0"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Playbooks
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-8">
        <FormField
          label="Playbook Name"
          helpText={formatHelpText(playbookFormHelpText.playbookName)}
          required
          error={errors.playbookName}
        >
          <Input
            value={pkg.playbookName}
            onChange={(e) => handleInputChange('playbookName', e.target.value)}
            placeholder="Enter playbook name"
            className={errors.playbookName ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Playbook Product Name"
          helpText={formatHelpText(playbookFormHelpText.playbookProductName)}
          required
          error={errors.playbookProductName}
        >
          <ProductSelect
            selectedProduct={pkg.playbookProductName}
            onChange={(productName) => handleInputChange('playbookProductName', productName)}
            readOnly={readOnly}
          />
        </FormField>

        <FormField
          label="Playbook Description"
          helpText={formatHelpText(playbookFormHelpText.playbookDescription)}
          required
          error={errors.playbookDescription}
        >
          <Textarea
            value={pkg.playbookDescription}
            onChange={(e) => handleInputChange('playbookDescription', e.target.value)}
            placeholder="Enter playbook description"
            rows={4}
            className={errors.playbookDescription ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Playbook Model"
          helpText="Choose the Large Language Model for your playbook. This will apply to all Agents in the swarm."
          required
          error={errors.playbookModel}
        >
          <Select
            value={pkg.playbookModel || 'gpt-4'}
            onValueChange={(value) => handleInputChange('playbookModel', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select playbook model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gpt-4">GPT-4</SelectItem>
              <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
              <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
              <SelectItem value="claude-2">Claude 2</SelectItem>
              <SelectItem value="claude-instant">Claude Instant</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label="Tags"
          helpText={formatHelpText(playbookFormHelpText.playbookTags)}
        >
          <TagManagement
            ref={tagManagementRef}
            tags={pkg.playbookTags}
            onChange={(newTags) => handleInputChange('playbookTags', newTags)}
            readOnly={false}
            suggestions={tagSuggestions}
            className="w-full"
          />
          <p className="text-sm text-muted-foreground mt-1">
            {/* Search existing tags or type to add new ones */}
          </p>
        </FormField>

        <FormField
          label="Playbook Workflow"
          helpText={formatHelpText(playbookFormHelpText.playbookWorkflow)}
          required
          error={errors.playbookWorkflow}
        >
          <Textarea
            value={pkg.playbookWorkflow}
            onChange={(e) => handleInputChange('playbookWorkflow', e.target.value)}
            placeholder="Enter playbook workflow"
            rows={4}
            className={errors.playbookWorkflow ? 'border-red-500' : ''}
          />
        </FormField>

        <FormField
          label="Special Instructions"
          helpText={formatHelpText(playbookFormHelpText.playbookSpecialInstructions)}
        >
          <Textarea
            value={pkg.playbookSpecialInstructions}
            onChange={(e) => handleInputChange('playbookSpecialInstructions', e.target.value)}
            placeholder="Enter special instructions"
            rows={3}
          />
        </FormField>

        <FormField
          label="Approval Notes"
          helpText={formatHelpText(playbookFormHelpText.playbookApprovalNotes)}
        >
          <Textarea
            value={pkg.playbookApprovalNotes}
            onChange={(e) => handleInputChange('playbookApprovalNotes', e.target.value)}
            placeholder="Enter approval notes"
            rows={3}
          />
        </FormField>

        <FormField
          label="Playbook Active Tools"
          helpText={formatHelpText(playbookFormHelpText.playbookActiveTools)}
          required
          error={errors.playbookActiveTools}
        >
          <ToolsSelect
            selectedTools={pkg.playbookActiveTools || []}
            onChange={(tools) => handleInputChange('playbookActiveTools', tools)}
            readOnly={readOnly}
          />
        </FormField>

        <FormField
          label="Playbook Helper Agents"
          helpText={formatHelpText(playbookFormHelpText.playbookHelperAgents)}
          required
          error={errors.playbookHelperAgents}
        >
          <AgentSelect
            selectedAgents={pkg.playbookHelperAgents || []}
            onChange={(agents) => handleInputChange('playbookHelperAgents', agents)}
            readOnly={readOnly}
          />
        </FormField>

        {isAdmin && (
          <div className="flex items-start space-x-2 border p-4 rounded-md bg-gray-50">
            <Checkbox
              id="core-playbook"
              checked={isCorePlaybook}
              onCheckedChange={handleCorePlaybookChange}
            />
            <div className="flex flex-col">
              <label
                htmlFor="core-playbook"
                className="text-sm font-medium cursor-pointer"
              >
                Core Playbook (Admin Only)
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Core playbooks have fixed access control set to "Owner Only", will be owned by the admin service account, and cannot be modified by regular users.
              </p>
            </div>
          </div>
        )}

        {!isCorePlaybook && (
          <AccessControlSection<Playbook>
            entity={pkg}
            entityType="Playbook"
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {isEditing && isAdmin && !isCorePlaybook && (
          <OwnerSelection
            currentOwnerId={pkg.ownerId}
            onOwnerChange={(newOwnerId) => handleInputChange('ownerId', newOwnerId)}
            isReadOnly={false}
          />
        )}

        {isEditing && isAdmin && isCorePlaybook && adminServiceUserId && (
          <div className="border p-4 rounded-md bg-blue-50">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Owner</h3>
            <p className="text-sm text-blue-700">
              This Core Playbook will be owned by the admin service account: <span className="font-semibold">{ADMIN_SERVICE_USER}</span>
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          {isEditing && onDiscardChanges && (
            <Button
              variant="outline"
              onClick={onDiscardChanges}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              disabled={props.isSaving}
            >
              Cancel Edit
            </Button>
          )}
          <Button
            variant="default"
            onClick={onSubmit}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            disabled={props.isSaving}
          >
            {props.isSaving ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            ) : (
              'Save and Test'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderViewMode = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="flex flex-col gap-4 pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full">
          <CardTitle className="text-2xl break-words max-w-full overflow-hidden text-ellipsis">
            {pkg.playbookName || 'Playbook Details'}
          </CardTitle>
          <div className="flex gap-2 mt-2 sm:mt-0 flex-shrink-0">
            {hasEditAccess(pkg.accessControl) && onEditClick && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEditClick}
                className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Playbooks
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-8 mt-2">
        <ViewModeField
          label="Playbook Product Name"
          value={pkg.playbookProductName}
        />

        <ViewModeField
          label="Description"
          value={pkg.playbookDescription}
        />

        <ViewModeField
          label="Playbook Model"
          value={pkg.playbookModel || 'gpt-4o'}
        />

        <ViewModeField
          label="Workflow"
          value={pkg.playbookWorkflow}
        />

        <ViewModeField
          label="Special Instructions"
          value={pkg.playbookSpecialInstructions || 'No special instructions provided'}
        />

        <ViewModeField
          label="Approval Notes"
          value={pkg.playbookApprovalNotes || 'No approval notes provided'}
        />

        <ViewModeField
          label="Active Tools"
          value={
            <ListDisplay
              items={pkg.playbookActiveTools || []}
              type="tool"
            />
          }
        />

        <ViewModeField
          label="Helper Agents"
          value={
            <ListDisplay
              items={pkg.playbookHelperAgents || []}
              type="agent"
            />
          }
        />

        <ViewModeField
          label="Tags"
          value={
            <TagManagement
              tags={pkg.playbookTags}
              onChange={() => { }}
              readOnly={true}
              suggestions={tagSuggestions}
            />
          }
        />

        {/* Show Access Control section for all playbooks, with special handling for Core Playbooks inside the component */}
        <AccessControlViewSection />

        {/* Execution Records Section */}
        <ViewModeField
          label="Execution Records"
          value={
            <ExecutionRecordsDisplay
              playbookId={pkg.playbookId}
              playbookName={pkg.playbookName}
              maxRecords={10}
              hidePlaybookName={true}
              hideOwner={true}
            />
          }
        />
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-4" data-has-unsaved-changes={hasUnsavedChanges}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="mb-8 space-y-1 ml-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {readOnly ? 'View Playbook' : 'Edit Playbook'}
          </h1>
          <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1">
            {readOnly
              ? 'View and test this playbook configuration.'
              : 'Update the playbook details and configuration.'}
          </p>
        </div>
        <>
          {readOnly && onEditClick && hasEditAccess(pkg.accessControl) && (
            <div className="mt-2 sm:mt-0">
              <Button
                onClick={() => onEditClick()}
                size="sm"
                className="w-full sm:w-auto"
              >
                <Pencil className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </Button>
            </div>
          )}
        </>
      </div>
      {props.isSaving && <SaveOverlay />}
      <div className="max-w-5xl mx-auto">
        {readOnly ? renderViewMode() : renderEditMode()}
      </div>
      {
        showDiscardDialog && (
          <DiscardChangesDialog
            isOpen={showDiscardDialog}
            onClose={() => setShowDiscardDialog(false)}
            onDiscard={handleDiscardChanges}
          />
        )
      }
    </div >
  );
});

export default PlaybookForm;